/**
 * Traverses up the dom from {el} and checks if a {parent} element exists
 *
 * @param {HTMLElement} el
 * @param {HTMLElement} parent
 */
export function hasParent(el, parent) {
    if (!el || !parent) {
        return false;
    }

    if (el === parent) {
        return true;
    }

    if (el.parentElement) {
        return hasParent(el.parentElement, parent);
    }

    return false;
}

/**
 * Finds parent element by selector or reference
 * @param el
 * @param {string|HTMLElement} parent Element selector or element
 * @return {HTMLElement} Element or null if no matching parent was found
 */
export function getParent(el, parent) {
    const matcher = typeof parent === "string"
        // Match selector if parent is string
        ? (el) => el.matches(parent)
        // Match element if parent is element
        : (el) => el === parent;

    if (el && matcher(el)) return el;

    if (el?.parentElement) {
        return getParent(el.parentElement, parent);
    }

    return null;
}


/**
 *
 * @param {string} value
 * @param {CSSStyleDeclaration} style
 * @return {DOMRect}
 */
export function measureText(value, style) {
    // Create rendering target if not existent
    if (!measureText.el) {
        measureText.el = createElementForTextMeasure();
    }

    // Apply styles
    measureText.el.style.font = style.font;
    measureText.el.style.textTransform = style.textTransform;

    // Set text content
    measureText.el.innerText = value;

    // Return text measurements
    return measureText.el.getBoundingClientRect();
}

/**
 * @param {CSSStyleDeclaration} styles
 * @return {(text: string) => DOMRect}
 */
export function measureTextSizeFactory(styles) {
    const el = createElementForTextMeasure();

    el.style.font = styles.font;
    el.style.lineHeight = styles.lineHeight;
    el.style.textTransform = styles.textTransform;

    return (text) => {
        el.innerText = text;
        return el.getBoundingClientRect();
    };
}

/**
 * Creates span for text measurement
 * @returns {HTMLSpanElement}
 */
function createElementForTextMeasure() {
    const el = document.createElement("span");
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";

    document.body.appendChild(el);

    return el;
}


/**
 * Adds class to element
 * @param {HTMLElement} el
 * @param {string|string[]} classes
 */
export function addClass(el, ...classes) {
    el.classList.add(...classes);
}

/**
 * Removes class from element
 * @param {HTMLElement} el
 * @param {string|string[]} classes
 */
export function removeClass(el, ...classes) {
   el.classList.remove(...classes);
}

/**
* Toggles classes on element
* @param {HTMLElement} el
* @param {string|string[]} classes
*/
export function toggleClass(el, ...classes) {
    classes.forEach((cls) => el.classList.toggle(cls));
}

/**
 * Returns if element has on or more classes
 * @param {HTMLElement} el
 * @param {string|string[]} classes
 */
export function hasClass(el, ...classes) {
    return classes.reduce((r, cls) => r && el.classList.contains(cls), true);
}

/**
 * Either returns or sets an attribute
 * @param {HTMLElement} el The element
 * @param {string} attr The attribute name
 * @param {string|undefined} value Optional value to set, if undefined no value is set
 */
export function attr(el, attr, value = undefined) {
    if (typeof attr === "undefined") {
        return el.getAttribute(attr);
    }

    return el.setAttribute(attr, value);
}


/**
 *
 * @param {HTMLElement} el
 * @param {keyof CSSStyleDeclaration} styleName
 * @param {string} value
 */
export function style(el, styleName, value) {
    el.style[styleName] = value;
}

/**
 * Returns if an element has a class that matches {pattern}
 * @param {HTMLElement} el
 * @param {RegExp} pattern
 */
export function get_class_regex(el, pattern) {
    return el.className.match(pattern);
}
